import { Box } from '@mafc/trolly';
import React from 'react';
import { useTranslation } from 'react-i18next';

import useApigeeLabels from 'common/ApigeeLabels/hooks/useApigeeLabels';
import OrderSummaryRow from 'common/OrderSummary/components/OrderSummaryRow';
import SummaryRowTypes from 'common/OrderSummary/components/OrderSummaryRow/row.types';
import { DeliveryCost, PlasticBagPackingInfo } from 'network/response/ICart';
import CardDivider from 'pages/Checkout/components/CheckoutOrderSummary/snippets/CardDivider';
import { IOrderConfirmationShipment } from 'pages/OrderConfirmation/OrderConfirmation.types';
import COLORS from 'theme/colors';
import { IFinalPrice } from 'utils/price.util';
import PackingFeeRow from 'common/PackingFeeRow';
import EPaymentMethods from 'pages/Checkout/components/Payment/PamentMethods.enum';

const FeeOrderSummary: React.FC<{
  codFee: DeliveryCost;
  sustainabilityConsent: boolean | null;
  paymentMode: string;
  plasticBagPackingInfo: PlasticBagPackingInfo;
  shipments: IOrderConfirmationShipment[];
  totalFees?: IFinalPrice;
}> = ({
  shipments,
  codFee,
  paymentMode,
  plasticBagPackingInfo,
  sustainabilityConsent,
  totalFees,
}) => {
  const { t } = useTranslation();
  const labels = useApigeeLabels();

  return totalFees ? (
    <Box mb={12}>
      <OrderSummaryRow
        label={labels.headerFees}
        priceDetails={totalFees}
        testId="total-fees"
        variant={SummaryRowTypes.header}
      />

      {/* Delivery fee */}

      {shipments.map((shipment: IOrderConfirmationShipment) => {
        const label = shipment.isFbc
          ? `${shipment.deliveryFeeLabel} (${t('fbc.Fbc')})`
          : shipment.deliveryFeeLabel;

        return (
          <OrderSummaryRow
            label={label}
            priceDetails={shipment.shipmentDeliveryFee || { value: 0 }}
            shouldShowFree
            testId={label}
            key={label}
          />
        );
      })}

      {/* Service fee */}

      {shipments.map((shipment: IOrderConfirmationShipment) => {
        return shipment.shipmentServiceFee ? (
          <OrderSummaryRow
            label={shipment.serviceFeeLabel}
            priceDetails={shipment.shipmentServiceFee}
            testId={shipment.serviceFeeLabel}
          />
        ) : (
          <></>
        );
      })}

      {/* Surcharge or slot fee */}

      {shipments.map((shipment: IOrderConfirmationShipment) => {
        return shipment.shipmentSurCharge ? (
          <OrderSummaryRow
            label={shipment.surchargeFeeLabel}
            priceDetails={shipment.shipmentSurCharge}
            testId={shipment.surchargeFeeLabel}
          />
        ) : (
          <></>
        );
      })}

      {/* Packing fee */}

      <PackingFeeRow
        plasticBagPackingInfo={plasticBagPackingInfo}
        sustainabilityConsent={sustainabilityConsent}
        label={labels.packagingFee}
      />

      {/* COD fee */}

      {paymentMode === EPaymentMethods.COD && codFee && !!codFee.value && (
        <OrderSummaryRow label={labels.codFee} priceDetails={codFee} testId={labels.codFee} />
      )}

      <CardDivider width={1} bg={COLORS.MERCURY} />
    </Box>
  ) : (
    <></>
  );
};

export default FeeOrderSummary;
